import {
  type DemoSessionActivity,
  type GetDemoSessionBreakdownArgs,
  useInfiniteScrollFromCache,
  useLazyGetDemoSessionsBreakdownQuery,
} from '@carboncopyco/data';
import { TextCaption } from '@carboncopyco/ui';
import {
  formatPlural,
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { SessionsBreakdownTable } from '../../../InfoPage/SessionsBreakdownTable';
import { useDashboardProps } from '../../DashboardContext';
import {
  DemosFilter,
  OpportunitiesFilter,
  PresentersFilter,
} from '../../SessionBreakdownComponents';
import type { CsvParams } from '../../SessionBreakdownComponents/types';
import { hasProperties } from '../../utils';
import { PresentationsTableCols } from './PresentationsTableCols';
import { useTableProps } from './TableContext';

export const PresentationsBreakdownTable = ({
  setCsvParams,
}: {
  setCsvParams: (params: CsvParams) => void;
}) => {
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    demoPagePath,
    playbookPagePath,
  } = useDashboardProps();
  const {
    presenters,
    opportunities,
    demos,
    setPresenters,
    setOpportunities,
    setDemos,
  } = useTableProps();
  const [showAll, setShowAll] = useState(false);

  const fetchArgs = useMemo<GetDemoSessionBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      opportunities: Object.keys(opportunities),
      demoIds: Object.keys(demos),
      presenters: Object.keys(presenters),
      withScreenshots: false,
      resourceTypes: ['managed_presentation', 'play'],
    }),
    [
      demos,
      internal,
      opportunities,
      organizationId,
      presenters,
      short,
      timeRange,
      selectedWorkspaceIds,
    ]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    DemoSessionActivity,
    GetDemoSessionBreakdownArgs
  >(useLazyGetDemoSessionsBreakdownQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  const clearFilters = () => {
    setPresenters({});
    setOpportunities({});
    setDemos({});
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['managed_presentation', 'play'],
      },
      isDisabled: (items && !items.length) || error,
    });
  }, [items, fetchArgs, error, setCsvParams]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <PresentersFilter />
          <OpportunitiesFilter />
          <DemosFilter />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable<DemoSessionActivity>
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        noResults={
          (hasProperties(presenters) ||
            hasProperties(opportunities) ||
            hasProperties(demos)) &&
          !items?.length
        }
        onClear={clearFilters}
        cols={PresentationsTableCols(demoPagePath, playbookPagePath)}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        error={error}
        handleFetchNextBatch={fetchNextBatch}
      />
    </>
  );
};
